import { CollapseButton, SidebarStatusActionTypes } from "@moznsystems/mozn-ui";
import { motion } from "framer-motion";
import { Fragment, useState } from "react";
import { matchPath, NavLink, useLocation } from "react-router-dom";
import { useSidebarStatusContext } from "store/sidebar-status-context";
import {
	GROUPS,
	getNavigationItems,
	type NavigationType,
} from "@/constants/navigation";

type SideBarProps = {
	setDrawerVisible: (status: boolean) => void;
};

const headerTabVariants = {
	open: {
		transition: { staggerChildren: 0.07, delayChildren: 0.05 },
	},
	closed: {
		transition: { staggerChildren: 0.05, staggerDirection: -1 },
	},
};

const headerSubTabVariants = {
	open: {
		y: 0,
		opacity: 1,
		transition: {
			y: { stiffness: 1000, velocity: -100 },
		},
	},
	closed: {
		y: 50,
		opacity: 0,
		transition: {
			y: { stiffness: 1000 },
		},
	},
};

export const SideBar = ({ setDrawerVisible }: SideBarProps) => {
	const location = useLocation();
	const active = location?.pathname?.split("/").join(",/").split(",");
	const [expandedItem, setExpandedItem] = useState<string | undefined>("/");
	const { sidebarStatus, handleSidebarStatus } = useSidebarStatusContext();

	let previousGroup = "";
	const toggleExpandItem = (url: string) => {
		setExpandedItem((prev) => (prev === url ? undefined : url));
	};

	return (
		<ul className="dashboard_menu">
			{getNavigationItems().map((i: NavigationType) => {
				const showGroup = i.group !== previousGroup;
				previousGroup = i.group;

				return (
					<Fragment key={i.url}>
						{showGroup && i.group !== GROUPS.MAIN && (
							<div className="side-bar__nav__group">
								<p className="side-bar__nav__group--title">{i.group}</p>
							</div>
						)}
						<li
							className={[
								"side-bar__nav__item",
								active.indexOf(i.url) !== -1 && "side-bar__nav__item--active",
							]
								.filter(Boolean)
								.join(" ")}
						>
							<div className="side-bar__nav__item--header">
								{i.subItems.length > 0 ? (
									<>
										<div
											className="side-bar__nav__item--main"
											key={i.url}
											onClick={() => {
												if (!i.subItems?.length) {
													setDrawerVisible(false);
												}
												toggleExpandItem(i.url);
											}}
										>
											<div>
												{i.icon && (
													<i.icon className="side-bar__nav__item__icon" />
												)}
												<span>{i.label}</span>
											</div>
										</div>
										<div onClick={(event) => event.stopPropagation()}>
											<CollapseButton
												collapsed={expandedItem !== i.url}
												toggleCollapse={() => toggleExpandItem(i.url)}
												iconStyle
											/>
										</div>
									</>
								) : (
									<NavLink
										key={i.url}
										onClick={() => {
											setDrawerVisible(false);
										}}
										className="side-bar__nav__item--main"
										to={i.url}
									>
										<div>
											{i.icon && (
												<i.icon className="side-bar__nav__item__icon" />
											)}
											<span>{i.label}</span>
										</div>
									</NavLink>
								)}
							</div>
							{i.subItems && i.subItems.length > 0 ? (
								<motion.ul
									variants={headerTabVariants}
									initial={false}
									animate={expandedItem === i.url ? "open" : "closed"}
									className={expandedItem === i.url ? "nav--expanded" : ""}
								>
									{i.subItems.map((subItem) => {
										const isActive = subItem.routes.some((routeURL) =>
											Boolean(
												matchPath(
													{
														path: routeURL,
														end: true,
													},
													location.pathname,
												),
											),
										);

										return (
											<motion.li
												variants={headerSubTabVariants}
												key={subItem.url}
												className={[
													"side-bar__nav__sub_item",
													isActive && "side-bar__nav__sub_item--active",
												]
													.filter(Boolean)
													.join(" ")}
											>
												<NavLink
													onClick={() => {
														setDrawerVisible(false);
														if (
															sidebarStatus ===
															SidebarStatusActionTypes.TEMPORARILY_OPEN_SIDEBAR
														) {
															handleSidebarStatus(
																SidebarStatusActionTypes.CLOSE_SIDEBAR,
															);
														}
													}}
													to={subItem.url}
												>
													<span>{subItem.label}</span>
												</NavLink>
											</motion.li>
										);
									})}
								</motion.ul>
							) : null}
						</li>
					</Fragment>
				);
			})}
		</ul>
	);
};
